import React, { useMemo, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { Link } from "react-router-dom";
import dayjs from "dayjs";
import { MdOutlineOpenInNew } from "react-icons/md";
import Sidebar from "../Sidebar";
import Layout from "../../layout/Layout";
import OrderDetailsModal from "../AdminBookings/OrderDetailsModal";
import { GET_BOOKINGS } from "../AdminBookings/graphql";
import { GET_MONEY_WATCH } from "./graphql";

// Where money can slip away: cash professionals still hold, bookings
// "paid online" outside Razorpay, COD the office later changed to online,
// unverified bookings, Razorpay payments
// nobody matched, and free money the office handed out.

const PRESETS = [
  { key: "last7", label: "7 days" },
  { key: "last30", label: "30 days" },
  { key: "last90", label: "90 days" },
];

const rupees = (v) => (v === null || v === undefined ? "—" : `₹${Math.round(v).toLocaleString("en-IN")}`);
const ageDays = (day) => (day ? dayjs().startOf("day").diff(dayjs(day), "day") : null);

const Card = ({ title, hint, right, children }) => (
  <section className="bg-white rounded-xl border border-gray-100 shadow-sm">
    <header className="flex flex-wrap items-start justify-between gap-2 px-4 pt-4">
      <div>
        <h2 className="font-semibold text-gray-900">{title}</h2>
        {hint && <p className="text-xs text-gray-500 mt-0.5">{hint}</p>}
      </div>
      {right}
    </header>
    <div className="p-4">{children}</div>
  </section>
);

const Kpi = ({ label, value, sub, alert }) => (
  <div className={`bg-white rounded-xl border p-4 shadow-sm ${alert ? "border-red-200" : "border-gray-100"}`}>
    <p className="text-xs uppercase tracking-wide text-gray-500">{label}</p>
    <p className={`text-3xl font-semibold mt-1 tabular-nums ${alert ? "text-red-600" : "text-gray-900"}`}>{value}</p>
    {sub && <p className="text-xs text-gray-500 mt-1">{sub}</p>}
  </div>
);

const Table = ({ head, children, empty, cols }) => (
  <div className="overflow-x-auto -mx-4">
    <table className="min-w-full text-sm">
      <thead>
        <tr className="text-left text-xs text-gray-500 border-b border-gray-100">
          {head.map((h) => (
            <th key={h} className="px-3 py-2 font-medium whitespace-nowrap first:pl-4">{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {children}
        {empty && (
          <tr>
            <td colSpan={cols || head.length} className="px-4 py-6 text-center text-gray-400">{empty}</td>
          </tr>
        )}
      </tbody>
    </table>
  </div>
);

const Td = ({ children, className = "" }) => <td className={`px-3 py-2 first:pl-4 tabular-nums ${className}`}>{children}</td>;

const MoneyWatch = () => {
  const [preset, setPreset] = useState("last7");
  const [fetchBooking, { loading: opening }] = useMutation(GET_BOOKINGS);
  const [viewingBooking, setViewingBooking] = useState(null);
  const [openError, setOpenError] = useState("");

  const range = useMemo(() => {
    const days = { last7: 6, last30: 29, last90: 89 }[preset];
    return { startDate: dayjs().subtract(days, "day").format("YYYY-MM-DD"), endDate: dayjs().format("YYYY-MM-DD") };
  }, [preset]);

  const { data, loading, error } = useQuery(GET_MONEY_WATCH, { variables: range, fetchPolicy: "cache-and-network" });
  const res = data?.getMoneyWatch;
  const d = res?.data;
  const errorMessage = error?.message || (res?.status === "ERROR" && res.message);

  const openBooking = async (r) => {
    setOpenError("");
    try {
      const out = await fetchBooking({ variables: { data: { id: r.orderId, bookingId: r.bookingId } } });
      const order = (out.data?.getBookings?.orders || []).find((o) => String(o.id) === String(r.orderId));
      if (!order) throw new Error(out.data?.getBookings?.message || "Booking not found");
      setViewingBooking(order);
    } catch (e) {
      setOpenError(`Could not open the booking: ${e.message}`);
    }
  };

  const cod = d?.codSettledOnline;
  const pct = (v) => `${(v || 0).toFixed(1)}%`;
  const maxMonth = Math.max(1, ...(cod?.months || []).map((m) => m.codAmount));

  const OpenBtn = ({ r }) => (
    <button type="button" disabled={opening} onClick={() => openBooking(r)} className="text-gray-400 hover:text-gray-900" title="Open booking">
      <MdOutlineOpenInNew />
    </button>
  );

  return (
    <div className="min-h-screen flex">
      <Sidebar />
      <Layout>
        <div className="max-w-7xl space-y-5 pb-16">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <h1 className="text-2xl font-semibold text-white">Money watch</h1>
              <p className="text-sm text-gray-400 mt-0.5">Money that hasn't reached the business yet, or left it without a clear reason.</p>
            </div>
            <div className="flex rounded-lg bg-white/10 p-0.5">
              {PRESETS.map((p) => (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => setPreset(p.key)}
                  className={`px-3 py-1.5 rounded-md text-sm ${preset === p.key ? "bg-white font-semibold text-gray-900" : "text-gray-300 hover:text-white"}`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          {errorMessage && <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm">Could not load money watch: {errorMessage}</div>}
          {openError && <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm">{openError}</div>}
          {loading && !d && <div className="py-16 text-center text-gray-400">Loading…</div>}

          {d && (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3">
                <Kpi
                  label="Cash with professionals"
                  value={rupees(d.cash.amount)}
                  sub={`${rupees(d.cash.overdueAmount)} older than ${d.thresholds.cashDays} days`}
                  alert={d.cash.overdueAmount > 0}
                />
                <Kpi
                  label="Paid online, not Razorpay"
                  value={rupees(d.onlineOutsideRazorpay.amount)}
                  sub={`${d.onlineOutsideRazorpay.bookings} bookings · ${d.onlineOutsideRazorpay.unverified} not verified`}
                  alert={d.onlineOutsideRazorpay.unverified > 0}
                />
                <Kpi label="Not verified" value={d.unverified.bookings} sub={`${rupees(d.unverified.amount)} · done ${d.thresholds.verifyDays}+ day ago`} alert={d.unverified.bookings > 0} />
                <Kpi label="Razorpay unmatched" value={rupees(d.unlinkedRazorpay.amount)} sub={`${d.unlinkedRazorpay.count} payments with no booking`} alert={d.unlinkedRazorpay.count > 0} />
                <Kpi label="Wallet credited" value={rupees(d.office.walletCredited)} sub="by the office, this period" />
                <Kpi label="Marked paid by hand" value={rupees(d.office.manualPayments)} sub="by the office, this period" />
              </div>

              <Card
                title="Cash with professionals"
                hint={`Completed bookings whose cash hasn't been settled with the office, however old. Overdue = older than ${d.thresholds.cashDays} days.`}
                right={<Link to="/admin/razorpay-payments" className="text-xs text-gray-500 hover:text-gray-900 underline">Settle via payment link</Link>}
              >
                <Table head={["Professional", "Holding", "Overdue", "Bookings", "Oldest"]} empty={!d.cash.employees.length && "Nobody is holding booking cash."}>
                  {d.cash.employees.map((c) => {
                    const age = ageDays(c.oldestDay);
                    return (
                      <tr key={c.employeeId || "none"} className="border-b border-gray-50">
                        <Td className="font-medium text-gray-900">{c.employeeName || "Unassigned"}</Td>
                        <Td className="font-semibold">{rupees(c.amount)}</Td>
                        <Td className={c.overdueAmount ? "text-red-600 font-semibold" : "text-gray-300"}>{rupees(c.overdueAmount)}</Td>
                        <Td>
                          {c.bookings}
                          {c.overdueBookings > 0 && <span className="text-xs text-red-600"> · {c.overdueBookings} overdue</span>}
                        </Td>
                        <Td className={age > 7 ? "text-red-600" : "text-gray-600"}>
                          {c.oldestDay ? `${dayjs(c.oldestDay).format("D MMM")} (${age}d)` : "—"}
                        </Td>
                      </tr>
                    );
                  })}
                </Table>
              </Card>

              <div className="grid lg:grid-cols-5 gap-5">
                <div className="lg:col-span-2">
                  <Card title="Paid online, not Razorpay — by professional" hint="Bookings marked 'Online' where the money didn't come through Razorpay. A steady pattern from one person is worth a call.">
                    <Table head={["Professional", "Bookings", "Amount"]} empty={!d.onlineOutsideRazorpay.employees.length && "None in this period."}>
                      {d.onlineOutsideRazorpay.employees.map((e) => (
                        <tr key={e.employeeId || "none"} className="border-b border-gray-50">
                          <Td className="font-medium text-gray-900">{e.employeeName || "Unassigned"}</Td>
                          <Td>{e.bookings}</Td>
                          <Td className="font-semibold">{rupees(e.amount)}</Td>
                        </tr>
                      ))}
                    </Table>
                  </Card>
                </div>
                <div className="lg:col-span-3">
                  <Card title="Those bookings" hint="Largest first. 'Not verified' means nobody in the office has checked the payment yet.">
                    <Table head={["Booking", "Day", "Professional", "Bill", "Outside Razorpay", "Checked?", ""]} empty={!d.onlineOutsideRazorpay.items.length && "None in this period."}>
                      {d.onlineOutsideRazorpay.items.map((o) => (
                        <tr key={o.orderId} className="border-b border-gray-50">
                          <Td className="font-mono text-xs">{o.bookingId}</Td>
                          <Td className="whitespace-nowrap">{dayjs(o.day).format("D MMM")}</Td>
                          <Td>{o.employeeName}</Td>
                          <Td>{rupees(o.price)}</Td>
                          <Td className="font-semibold">{rupees(o.outsideRazorpay)}</Td>
                          <Td>{o.verificationStatus === "VERIFIED" ? <span className="text-emerald-700">Verified</span> : <span className="text-red-600">Not verified</span>}</Td>
                          <Td><OpenBtn r={o} /></Td>
                        </tr>
                      ))}
                    </Table>
                  </Card>
                </div>
              </div>

              <Card
                title="Marked COD, settled online"
                hint="The professional marked the booking as cash (COD), and the office later changed it to Online at verification: the customer paid the admin account directly, not through Razorpay. Amounts leave out anything Razorpay captured."
              >
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
                  <div>
                    <p className="text-xs text-gray-500">Marked COD by professionals</p>
                    <p className="text-xl font-semibold tabular-nums">{rupees(cod.codAmount)}</p>
                    <p className="text-xs text-gray-500">{cod.codBookings} bookings</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Changed to online by office</p>
                    <p className="text-xl font-semibold tabular-nums text-amber-600">{rupees(cod.onlineAmount)}</p>
                    <p className="text-xs text-gray-500">{cod.onlineBookings} bookings · {pct(cod.onlineShare)} of COD</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Stayed cash</p>
                    <p className="text-xl font-semibold tabular-nums">{rupees(cod.stayedCashAmount)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Marked online, changed to cash</p>
                    <p className="text-xl font-semibold tabular-nums">{rupees(cod.toCashAmount)}</p>
                    <p className="text-xs text-gray-500">{cod.toCashBookings} bookings</p>
                  </div>
                </div>

                <div className="grid lg:grid-cols-5 gap-5">
                  <div className="lg:col-span-2 space-y-5">
                    <div>
                      <h3 className="text-sm font-medium text-gray-700 mb-2">By professional</h3>
                      <Table head={["Professional", "Marked COD", "→ Online", "%"]} empty={!cod.employees.length && "No COD bookings in this period."}>
                        {cod.employees.map((e) => (
                          <tr key={e.employeeId || "none"} className="border-b border-gray-50">
                            <Td className="font-medium text-gray-900">{e.employeeName || "Unassigned"}</Td>
                            <Td>
                              {rupees(e.codAmount)} <span className="text-xs text-gray-400">({e.codBookings})</span>
                            </Td>
                            <Td className={e.onlineAmount ? "font-semibold text-amber-600" : "text-gray-300"}>
                              {rupees(e.onlineAmount)} <span className="text-xs text-gray-400">({e.onlineBookings})</span>
                            </Td>
                            <Td className={e.onlineShare >= 40 ? "text-red-600 font-semibold" : "text-gray-600"}>{pct(e.onlineShare)}</Td>
                          </tr>
                        ))}
                      </Table>
                    </div>
                    <div>
                      <h3 className="text-sm font-medium text-gray-700 mb-2">Last 12 months</h3>
                      <div className="space-y-1.5">
                        {cod.months.map((m) => (
                          <div key={m.month} className="flex items-center gap-2 text-xs">
                            <span className="w-14 text-gray-500 shrink-0">{dayjs(`${m.month}-01`).format("MMM YY")}</span>
                            <div className="flex-1 h-3 bg-gray-100 rounded-sm overflow-hidden" title={`COD ${rupees(m.codAmount)} · online ${rupees(m.onlineAmount)}`}>
                              <div className="h-full bg-gray-300 relative" style={{ width: `${(100 * m.codAmount) / maxMonth}%` }}>
                                <div className="absolute inset-y-0 left-0 bg-amber-500" style={{ width: `${m.codAmount ? (100 * m.onlineAmount) / m.codAmount : 0}%` }} />
                              </div>
                            </div>
                            <span className="w-24 text-right tabular-nums text-gray-700 shrink-0">{rupees(m.onlineAmount)}</span>
                            <span className="w-11 text-right tabular-nums text-gray-500 shrink-0">{pct(m.onlineShare)}</span>
                          </div>
                        ))}
                      </div>
                      <p className="text-[11px] text-gray-400 mt-2">
                        <span className="inline-block w-2 h-2 bg-amber-500 rounded-sm mr-1" />changed to online
                        <span className="inline-block w-2 h-2 bg-gray-300 rounded-sm ml-3 mr-1" />stayed cash · bar = COD marked that month
                      </p>
                    </div>
                  </div>
                  <div className="lg:col-span-3">
                    <h3 className="text-sm font-medium text-gray-700 mb-2">Those bookings (latest first)</h3>
                    <Table head={["Booking", "Day", "Professional", "Marked", "Bill", "Paid online", ""]} empty={!cod.items.length && "None in this period."}>
                      {cod.items.map((o) => (
                        <tr key={o.orderId} className="border-b border-gray-50">
                          <Td className="font-mono text-xs">{o.bookingId}</Td>
                          <Td className="whitespace-nowrap">{dayjs(o.day).format("D MMM")}</Td>
                          <Td>{o.employeeName}</Td>
                          <Td className="text-xs text-gray-600">{o.paymentType}</Td>
                          <Td>{rupees(o.price)}</Td>
                          <Td className="font-semibold">{rupees(o.outsideRazorpay)}</Td>
                          <Td><OpenBtn r={o} /></Td>
                        </tr>
                      ))}
                    </Table>
                  </div>
                </div>
              </Card>

              <div className="grid lg:grid-cols-2 gap-5">
                <Card title="Completed but not verified" hint={`Done ${d.thresholds.verifyDays}+ day ago and nobody has verified the payment (last 60 days).`}>
                  <Table head={["Booking", "Day", "Professional", "Bill", "Paid by", ""]} empty={!d.unverified.items.length && "Everything is verified."}>
                    {d.unverified.items.map((o) => (
                      <tr key={o.orderId} className="border-b border-gray-50">
                        <Td className="font-mono text-xs">{o.bookingId}</Td>
                        <Td className="whitespace-nowrap">
                          {dayjs(o.day).format("D MMM")} <span className="text-xs text-gray-400">({ageDays(o.day)}d)</span>
                        </Td>
                        <Td>{o.employeeName}</Td>
                        <Td>{rupees(o.price)}</Td>
                        <Td>{o.paymentType || "—"}</Td>
                        <Td><OpenBtn r={o} /></Td>
                      </tr>
                    ))}
                  </Table>
                </Card>

                <Card
                  title="Razorpay payments with no booking"
                  hint="Money that arrived but isn't attached to a booking or a cash deposit yet."
                  right={<Link to="/admin/razorpay-payments" className="text-xs text-gray-500 hover:text-gray-900 underline">Match them</Link>}
                >
                  <Table head={["Paid", "Amount", "Method", "From", "Looks like"]} empty={!d.unlinkedRazorpay.items.length && "All matched."}>
                    {d.unlinkedRazorpay.items.map((u) => (
                      <tr key={u.id} className="border-b border-gray-50">
                        <Td className="whitespace-nowrap">{u.paidAt ? dayjs(u.paidAt).format("D MMM, h:mm A") : "—"}</Td>
                        <Td className="font-semibold">{rupees(u.amount)}</Td>
                        <Td>{u.method || "—"}</Td>
                        <Td className="text-xs text-gray-600">{u.vpa || u.contact || "—"}</Td>
                        <Td className="text-xs text-gray-600">{u.suggestedKind ? u.suggestedKind.replace(/_/g, " ").toLowerCase() : "—"}</Td>
                      </tr>
                    ))}
                  </Table>
                </Card>
              </div>

              <Card
                title="Money handed out by the office"
                hint="Wallet credits and payments attached by hand, by person, this period."
                right={<Link to="/admin/office-activity" className="text-xs text-gray-500 hover:text-gray-900 underline">See every change</Link>}
              >
                <Table head={["Person", "Wallet credited", "Credits", "Marked paid by hand", "Times", "Ledger edits/deletes"]} empty={!d.office.people.length && "No money actions by the office in this period."}>
                  {d.office.people.map((p) => (
                    <tr key={p.actorId || "none"} className="border-b border-gray-50">
                      <Td className="font-medium text-gray-900">{p.actorName || "Unknown"}</Td>
                      <Td className={p.walletCredited ? "text-amber-600 font-semibold" : "text-gray-300"}>{rupees(p.walletCredited)}</Td>
                      <Td>{p.walletCredits}</Td>
                      <Td>{rupees(p.manualPayments)}</Td>
                      <Td>{p.manualPaymentCount}</Td>
                      <Td className={p.ledgerEdits ? "text-red-600" : "text-gray-300"}>{p.ledgerEdits}</Td>
                    </tr>
                  ))}
                </Table>
              </Card>
            </>
          )}
        </div>
      </Layout>
      {viewingBooking && <OrderDetailsModal booking={viewingBooking} onClose={() => setViewingBooking(null)} />}
    </div>
  );
};

export default MoneyWatch;
